import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Pressable } from 'react-native';
import styled from 'styled-components/native';
import moment from 'moment';
import _ from 'lodash';
import DropDown from './drop-down';
import createDaysArray from './create-days-array';
import Modal from '../modal';
import Colors from '../colors';
import Spacer from '../spacer';
import Text from '../text';
const WEEKDAYS = ['Mo', 'Tu', 'We', 'Th', 'Fri', 'Sa', 'Su'];
const Container = styled.View`
  padding-horizontal: 32px;
  padding-vertical: 24px;
`;
const DropDownContainer = styled.View`
  flex-direction: row;
  z-index: 9999;
  align-self: center;
`;
const DaysContainer = styled.View`
  flex-direction: row;
  flex-wrap: wrap;
`;
const DaysItem = styled.Pressable`
  align-items: center;
  justify-content: center;
  height: 40px;
  width: 14.28%;
  cursor: ${_ref => {
  let {
    disabledPointer
  } = _ref;
  return disabledPointer ? 'not-allowed' : 'pointer';
}};
`;
const DayComponent = styled.View`
  height: 40px;
  width: 40px;
  border-radius: 20px;
  align-items: center;
  justify-content: center;
  background-color: ${_ref2 => {
  let {
    backgroundColor
  } = _ref2;
  return backgroundColor;
}};
`;
const ButtonsContainer = styled.View`
  flex-direction: row;
`;
const CancelButton = styled.Pressable`
  border-radius: 8px;
  padding: 8px;
  flex: 1;
  align-items: center;
  background-color: ${Colors.lightGray200};
`;
const SubmitButton = styled.Pressable`
  border-radius: 8px;
  padding: 8px;
  flex: 1;
  align-items: center;
  background-color: ${_ref3 => {
  let {
    disabled
  } = _ref3;
  return disabled ? Colors.lightGray200 : Colors.advanceBlue400;
}};
`;
export default function DatePicker(props) {
  const {
    children,
    onChange,
    value,
    minimumDate,
    maximumDate,
    disabledDates,
    disabledDays
  } = props;
  const [active, setActive] = useState(false);
  const diff = moment(maximumDate).year() - moment(minimumDate).year() + 1;
  const years = Array.from({
    length: diff
  }, (_, i) => maximumDate.getFullYear() - i);
  const reverseYears = years.slice().reverse();
  const defaultDate = props.defaultSelectedDate ? moment(props.defaultSelectedDate).format('YYYY-MM-DD') : value || [_.head(years), moment().format('MM'), moment().format('DD')].join('-');
  const [year, setYear] = useState(moment(defaultDate).format('YYYY'));
  const [month, setMonth] = useState(moment(defaultDate).format('MM'));
  const [day, setDay] = useState(moment(defaultDate).format('DD'));
  const daysArray = createDaysArray(month, year, minimumDate, maximumDate, disabledDates, disabledDays);

  const getConstructedDate = () => {
    const thirtyDayMonths = ['04', '06', '09', '11'];

    if (!moment(year).isLeapYear() && month === '02' && day === '29') {
      setDay('28');
      return moment(`${year}-${month}-28`, 'YYYY-MM-DD');
    }

    if (thirtyDayMonths.includes(month) && day === '31') {
      setDay('30');
      return moment(`${year}-${month}-30`, 'YYYY-MM-DD');
    }

    const constructedDate = moment(`${year}-${month}-${day}`, 'YYYY-MM-DD');

    if (!constructedDate.isValid()) {
      setYear(moment(maximumDate).format('YYYY'));
      setMonth(moment(maximumDate).format('MM'));
      setDay(moment(maximumDate).format('DD'));
      return moment(maximumDate).format('YYYY-MM-DD');
    }

    return constructedDate;
  };

  const isDateWithinRange = moment(getConstructedDate()).isBetween(minimumDate, maximumDate, 'days', '[]');
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Modal, {
    active: active,
    activeSetter: setActive,
    width: 344
  }, /*#__PURE__*/React.createElement(Container, {
    onStartShouldSetResponder: () => true
  }, /*#__PURE__*/React.createElement(DropDownContainer, null, /*#__PURE__*/React.createElement(DropDown, {
    value: month,
    onChange: setMonth,
    items: moment.months().map((month, index) => ({
      label: month,
      value: _.padStart(_.toString(index + 1), 2, '0')
    }))
  }), /*#__PURE__*/React.createElement(DropDown, {
    value: year,
    onChange: setYear,
    items: (props.reverseYears ? reverseYears : years).map(year => ({
      label: year,
      value: year
    }))
  })), /*#__PURE__*/React.createElement(DaysContainer, null, WEEKDAYS.map((weekday, index) => /*#__PURE__*/React.createElement(DaysItem, {
    key: index,
    disabled: true
  }, /*#__PURE__*/React.createElement(Text.BodySmall, null, weekday))), daysArray.map((_ref4, index) => {
    let {
      date,
      state
    } = _ref4;
    const disabled = state === 'disabled';
    const today = state === 'today';
    const selected = moment(getConstructedDate()).isSame(moment(date.dateString), 'day');
    const allowed = moment(date.dateString).isBetween(minimumDate, maximumDate, 'days', '[]');
    const isDisabledDate = disabledDates.some(disabledDate => moment(disabledDate, 'YYYY-MM-DD').isSame(moment(date.dateString), 'day'));
    const isDisabledDay = disabledDays.includes(moment(date.dateString).isoWeekday());
    return /*#__PURE__*/React.createElement(DaysItem, {
      key: index,
      disabledPointer: isDisabledDate || isDisabledDay,
      disabled: !allowed || isDisabledDate,
      onPress: () => {
        setYear(date.year);
        setMonth(date.month);
        setDay(date.day);
      }
    }, /*#__PURE__*/React.createElement(DayComponent, {
      backgroundColor: selected ? Colors.advanceBlue400 : today ? Colors.lightGray200 : Colors.transparent
    }, /*#__PURE__*/React.createElement(Text.BodySmall, {
      color: selected ? Colors.white : today ? Colors.advanceBlue400 : disabled ? Colors.gray300 : Colors.gray600
    }, date.day)));
  })), /*#__PURE__*/React.createElement(Spacer, {
    size: "f24",
    height: true
  }), /*#__PURE__*/React.createElement(ButtonsContainer, null, /*#__PURE__*/React.createElement(CancelButton, {
    onPress: () => {
      setYear(moment(defaultDate).format('YYYY'));
      setMonth(moment(defaultDate).format('MM'));
      setDay(moment(defaultDate).format('DD'));
      setActive(false);
    }
  }, /*#__PURE__*/React.createElement(Text.BodySmall, null, "Cancel")), /*#__PURE__*/React.createElement(Spacer, {
    size: "f8",
    width: true
  }), /*#__PURE__*/React.createElement(SubmitButton, {
    onPress: () => {
      onChange(moment(getConstructedDate()).toDate());
      setActive(false);
    },
    disabled: !isDateWithinRange && props.disabledSetButton
  }, /*#__PURE__*/React.createElement(Text.BodySmall, {
    color: Colors.white
  }, "Set Date"))))), /*#__PURE__*/React.createElement(Pressable, {
    onPress: () => setActive(true)
  }, children));
}
DatePicker.defaultProps = {
  children: null,
  onChange: () => null,
  value: moment().toDate(),
  defaultSelectedDate: null,
  disabledSetButton: false,
  reverseYears: false,
  minimumDate: moment().subtract(100, 'years').toDate(),
  maximumDate: moment().add(100, 'years').toDate(),
  disabledDays: [],
  disabledDates: []
};
DatePicker.propTypes = {
  children: PropTypes.node,
  onChange: PropTypes.func,
  defaultSelectedDate: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.oneOf([null])]),
  disabledSetButton: PropTypes.bool,
  reverseYears: PropTypes.bool,
  value: PropTypes.instanceOf(Date),
  minimumDate: PropTypes.instanceOf(Date),
  maximumDate: PropTypes.instanceOf(Date),
  disabledDays: PropTypes.arrayOf(PropTypes.number),
  disabledDates: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string]))
};
//# sourceMappingURL=index.js.map